import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  addCarServiceData,
  AddCarServiceSchema,
  AddCarServiceSchemaType,
} from "@/services/addCarService";

import { ArrowLeft, ChevronLeft } from "lucide-react";

import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Role } from "@/core/auth/models/role.model";
import withAuthorization from "@/core/auth/components/withAuthorization";
import { fetchCategories } from "@/services/getCategories";
import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";


function AddCarService() {
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const [subcategories, setSubcategories] = useState([]);

  const addCarServiceForm = useForm<AddCarServiceSchemaType>({
    resolver: zodResolver(AddCarServiceSchema),
    defaultValues: {
      name: "",
      description: "",
      price: "",
      category: "",
      subCategory: "",
      duration: "",
      company: user?.company?._id,
      available: true,
    },
  });

  const navigate = useNavigate();
  
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["categories"] });
  }, [queryClient]);
  
  const CategoryData = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const data = await fetchCategories();
      return data.map((category: any) => {
        return { ...category, company: category.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });


  const addCarServiceMutation = useMutation({
    mutationFn: async (formData: AddCarServiceSchemaType) => {
      if (!formData.subCategory) {
        delete formData.subCategory;
      }
      return addCarServiceData(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["car-services"] });
      toast.success("Add car service successful");
      navigate("/app/car-services");
    },
    onError: (error) => {
      const newError = error as any;
      toast.error(newError.response?.data?.message ?? "An error occured");
      console.error("An error occurred while adding the order:", error);
    },
  });

  const { isPending } = addCarServiceMutation;

  const onSubmit = (values: AddCarServiceSchemaType) => {
    addCarServiceMutation.mutate(values);
  };

  const handleCategoryChange = (categoryId: string) => {
    const selectedCategory = CategoryData.data?.find(
      (category: { _id: any }) => category._id === categoryId
    );
    setSubcategories(selectedCategory?.subcategories || []);
    addCarServiceForm.setValue("category", categoryId);
  };

  return (
    <div className="w-full p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-8">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Link
              to="/app/car-services"
              className={buttonVariants({ variant: "ghost", size: "sm" })}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {/* Back */}
            </Link>
            <h1 className="text-2xl font-bold tracking-tight">
              Add New Service
            </h1>
          </div>
          <p className="text-muted-foreground">
            Create a new car service for your business
          </p>
        </div>
      </div>

      {/* Main Form Section */}
      <div className="mb-4">
        <Form {...addCarServiceForm}>
          <form onSubmit={addCarServiceForm.handleSubmit(onSubmit)}>
            <div className="space-y-8">
                 {/* Category Selection Card */}
                 <div className="bg-card border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Categorization</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="w-full">
                    <FormField
                      control={addCarServiceForm.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category</FormLabel>
                          <Select
                            onValueChange={handleCategoryChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {CategoryData.data?.map((category) => (
                                <SelectItem
                                  key={category._id}
                                  value={category._id}
                                >
                                  {category.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Choose the main category for this service
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-full">
                    <FormField
                      control={addCarServiceForm.control}
                      name="subCategory"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subcategory</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={!subcategories.length}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={
                                    subcategories.length
                                      ? "Select a subcategory"
                                      : "Select a category first"
                                  }
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {subcategories.map((subcategory) => (
                                <SelectItem
                                  key={subcategory._id}
                                  value={subcategory._id}
                                >
                                  {subcategory.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Choose a specific subcategory (optional)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
              {/* Service Details Card */}
              <div className="bg-card border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Service Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="w-full">
                    <FormField
                      control={addCarServiceForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Service Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter service name"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Give your service a clear and descriptive name
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-full">
                    <FormField
                      control={addCarServiceForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            {/* <Input placeholder="Enter service description" {...field} /> */}
                            <Textarea
                              placeholder="Enter service description"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Briefly describe what this service includes
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Pricing and Duration Card */}
              <div className="bg-card border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">
                  Pricing & Duration
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="w-full">
                    <FormField
                      control={addCarServiceForm.control}
                      name="price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price (KSH)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0.00"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-full">
                    <FormField
                      control={addCarServiceForm.control}
                      name="duration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duration (minutes)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="30" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

           

              {/* Submit Button */}
              <div className="flex justify-end">
                <Button type="submit" className="px-8" disabled={isPending}>
                  {isPending ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Adding...
                    </>
                  ) : (
                    "Add Service"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

const AddCarServicePage = withAuthorization(AddCarService, [Role.CompanyAdmin]);

export default AddCarServicePage;
